import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { repeat } from 'lit/directives/repeat.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './compare-popup.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import type { CompareProduct } from 'components/discovery/compare/compare-events.js'
import { CscCompareRejectedEvent, CscCompareRemoveEvent, CscCompareClearEvent, CscCompareNavigateEvent } from 'components/discovery/compare/compare-events.js'
import { clearCompareSelection, normalizeCompareSelection, readCompareSelection, writeCompareSelection } from 'components/discovery/compare/compare-selection-storage.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'

initializeIconsByRole({
  'csc-compare-popup.toggle-down': { name: 'angle-down', style: 'regular' },
  'csc-compare-popup.toggle-up': { name: 'angle-up', style: 'regular' },
  'csc-compare-popup.error': { name: 'exclamation-circle', style: 'regular' },
  'csc-compare-popup.remove': { name: 'close', style: 'light' },
})

const MAX_ITEMS = 4
const MIN_ITEMS_TO_SHOW = 1

@customElement('csc-compare-popup')
export class CSCComparePopup extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ attribute: 'popup-position' })
  popupPosition: 'left' | 'center' | 'right' = 'right'

  @property({ type: Boolean, attribute: 'display-prices' })
  displayPrices = false

  @state()
  private items: CompareProduct[] = []

  @state()
  private open = false

  @state()
  private showMaxError = false

  @state()
  private removingIds = new Set<number>()

  @state()
  private dismissing = false

  private persistItems(): void {
    writeCompareSelection(this.items)
  }

  private hydrateFromStorage(): void {
    const stored = readCompareSelection()
    this.items = normalizeCompareSelection(stored, MAX_ITEMS)
    if (this.items.length >= MIN_ITEMS_TO_SHOW) {
      this.open = true
    } else {
      this.open = false
    }
  }

  /** Re-sync after back/forward navigation (selection is global, not path-scoped). */
  private handleListingNavigation = (): void => {
    this.hydrateFromStorage()
  }

  private handleExternalRemove = (e: CustomEvent<{ id: number }>) => {
    const id = e.detail.id
    if (this.removingIds.has(id)) return
    if (!this.items.some((item) => item.id === id)) return
    this.items = this.items.filter((item) => item.id !== id)
    this.showMaxError = false
    this.open = this.items.length >= MIN_ITEMS_TO_SHOW
    this.persistItems()
  }

  private handleExternalClear = () => {
    if (this.dismissing) return
    this.items = []
    this.showMaxError = false
    this.open = false
  }

  private handleToggle = (e: Event) => {
    const { product, checked } = (e as CustomEvent<{ product: CompareProduct; checked: boolean }>).detail
    if (checked) {
      if (this.items.length < MAX_ITEMS) {
        this.items = [...this.items, product]
        this.showMaxError = false
        this.persistItems()
        this.open = true
      } else {
        this.showMaxError = true
        this.open = true
        window.dispatchEvent(new CscCompareRejectedEvent(product.id))
      }
    } else if (this.items.some(item => item.id === product.id)) {
      this.animateRemove(product.id)
    }
  }

  override connectedCallback() {
    super.connectedCallback()
    window.addEventListener('csc-compare-toggle', this.handleToggle)
    window.addEventListener('csc-compare-remove', this.handleExternalRemove as EventListener)
    window.addEventListener('csc-compare-clear', this.handleExternalClear)
    window.addEventListener('popstate', this.handleListingNavigation)
    this.hydrateFromStorage()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('csc-compare-toggle', this.handleToggle)
    window.removeEventListener('csc-compare-remove', this.handleExternalRemove as EventListener)
    window.removeEventListener('csc-compare-clear', this.handleExternalClear)
    window.removeEventListener('popstate', this.handleListingNavigation)
  }

  private animateRemove(id: number, onComplete?: () => void) {
    this.removingIds = new Set([...this.removingIds, id])
    setTimeout(() => {
      this.items = this.items.filter(item => item.id !== id)
      this.removingIds.delete(id)
      this.removingIds = new Set(this.removingIds)
      this.showMaxError = false
      if (this.items.length >= MIN_ITEMS_TO_SHOW) {
        this.open = true
      } else {
        this.open = false
      }
      this.persistItems()
      onComplete?.()
    }, 300)
  }

  private removeItem(id: number) {
    this.animateRemove(id)
    window.dispatchEvent(new CscCompareRemoveEvent(id))
  }

  private clearAll() {
    this.dismissing = true
    window.dispatchEvent(new CscCompareClearEvent())
    setTimeout(() => {
      this.items = []
      this.showMaxError = false
      this.dismissing = false
      this.open = false
      clearCompareSelection()
    }, 300)
  }

  private toggleOpen() {
    this.open = !this.open
  }

  private async navigateToComparePage() {
    if (this.items.length < 2) return
    const params = this.items.map(item => `id=${item.id}`).join('&')
    const url = `${(await createUrl('compare/')).href}?${params}`
    const cancelled = !window.dispatchEvent(new CscCompareNavigateEvent(url))
    if (!cancelled) window.location.href = url
  }

  override render() {
    if (this.items.length < MIN_ITEMS_TO_SHOW) return nothing

    const classes = {
      'csc-compare-popup': true,
      'is-open': this.open,
      'is-dismissing': this.dismissing,
      [`position-${this.popupPosition}`]: true,
    }

    return html`
      <div class=${classMap(classes)}>
        <div class="header">
          <div class="header-left" @click=${this.toggleOpen}>
            <cse-icon icon-role="${this.open ? 'csc-compare-popup.toggle-down' : 'csc-compare-popup.toggle-up'}"></cse-icon>
            <cse-text variant="heading-5">${t('title', { ns: 'csc-compare-popup', default: 'Compare' })}</cse-text>
            <cse-badge variant="warning" composition="floating" size="default">${this.items.length}/${MAX_ITEMS}</cse-badge>
          </div>
          <cse-link 
            @click=${(e: Event) => { e.stopPropagation(); this.clearAll() }}
            variant=${this.open ? 'primary' : 'white'}
        >
            ${t('clear_all', { ns: 'csc-compare-popup', default: 'Clear all' })}
          </cse-link>
        </div>

        <div class=${classMap({ collapsible: true, 'is-open': this.open })}>
          <div class="collapsible-inner">
            <div class="items">
                <cse-divider orientation="horizontal" variant="solid"></cse-divider>
                ${this.showMaxError ? html`
                  <cse-text class="max-error" has-icon>
                    <cse-icon slot="prefix" icon-role="csc-compare-popup.error"></cse-icon>
                    ${t('max_error', { ns: 'csc-compare-popup', default: 'Maximum 4 products' })}
                  </cse-text>
                ` : nothing}
                ${repeat(this.items, item => item.id, item => html`
                <div class="item ${this.removingIds.has(item.id) ? 'item-removing' : ''}">
                    <csc-product-preview
                        product-name="${item.name}"
                        product-id="${item.code}"
                        price="${this.displayPrices ? item.price : ''}"
                        image-source="${item.image}"
                        size="small"
                    ></csc-product-preview>
                    <cse-icon-button
                        variant="primary"
                        size="default"
                        @click=${() => this.removeItem(item.id)}
                        aria-label="Remove ${item.name}"
                    >
                        <cse-icon icon-role="csc-compare-popup.remove"></cse-icon>
                    </cse-icon-button>
                </div>
                `)}
            </div>
            <div class="footer">
              <cse-button
                variant="primary"
                fit="fill"
                ?disabled=${this.items.length < 2}
                @click=${this.navigateToComparePage}
                >
                ${t('compare_now', { ns: 'csc-compare-popup', default: 'Compare now' })}
              </cse-button>
            </div>
          </div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-compare-popup': CSCComparePopup
  }
}
